import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import api from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import ProductCard from "@/components/ProductCard";
import { useSectionContent } from "@/hooks/useContent";

const FILTERS = ["Tutti", "T-Shirt", "Felpe", "Giacche", "Cappellini"];

export default function MerchShop() {
  const [filter, setFilter] = useState("Tutti");
  const c = useSectionContent("merch");

  const { data: products = [], isLoading, isError } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  const visible = useMemo(
    () => (filter === "Tutti" ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );

  return (
    <section
      id="merch"
      data-testid="merch-section"
      className="py-24 sm:py-32 px-5 sm:px-8 bg-[#121215]"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          overline={c.overline}
          title={c.title}
          description={c.description}
          testid="merch-heading"
        />

        <div className="mt-10 flex flex-wrap gap-2" data-testid="merch-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
                filter === f
                  ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0B] font-bold"
                  : "border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059]/60 hover:text-[#F4EBE1]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="leather-card aspect-[3/4] animate-pulse" />
            ))}
          </div>
        )}

        {isError && (
          <p data-testid="merch-error" className="mt-14 text-[#9E1B1B] font-mono text-sm">
            Errore nel caricamento dei prodotti. Riprova tra poco.
          </p>
        )}

        {!isLoading && !isError && (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="products-grid">
            {visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}

        <div className="mt-10 flex items-start gap-3 leather-card p-5 max-w-2xl" data-testid="demo-checkout-notice">
          <Info className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" />
          <p className="text-xs text-[#C0B9B0] leading-relaxed">{c.notice}</p>
        </div>
      </div>
    </section>
  );
}
